import { Injectable } from '@angular/core';
import { AuthUser } from '@app/account/account-api.model';
import { Observable, of, tap } from 'rxjs';
import { ApplicationId } from '../web-environment-info';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
    Osano: any;
  }
}

@Injectable({
  providedIn: 'root',
})
export class VendorIntegrationsService {
  /**
   * Initializes vendor integrations, including the Osano cookie handler, and analytics tracking.
   *
   * This method determines whether the Osano cookie handler is required for the given authenticated user.
   * - If Osano is **not required**, it immediately initializes analytics tracking.
   * - If Osano **is required**, it loads the Osano cookie handler asynchronously before initializing analytics tracking.
   *
   * @param {AuthUser} [authUser] Optional authenticated user object.
   * @returns {Observable<void>} An observable that completes when the initialization process is finished.
   */
  public initialize(
    authUser?: AuthUser,
    applicationId?: ApplicationId,
    osanoEnabled?: boolean
  ): Observable<boolean> {
    if (applicationId !== ApplicationId.LXP) {
      return of(true);
    }

    const initializeServices = () => {
      this.initializeAnalyticsTracking(authUser);
    };

    if (osanoEnabled || this.osanoIsRequired(authUser)) {
      return this.loadOsanoCookieHandler(true).pipe(tap(initializeServices));
    }

    return of(true).pipe(tap(initializeServices));
  }

  /**
   * Loads the osano cookie tracker script.
   *
   * Osano managers whether or not a user of the site has chosen to accept cookies or not. It is the primary
   * method which degreed manages this data. There is a degreed fallback if for some reason a client doesn't
   * allow us to use third party vendors based on the 'enableOsanoCookieBannerAuthed' flag on the authUser object.
   */

  public loadOsanoCookieHandler(
    offsetIcon: boolean = false
  ): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      const scriptTag = document.createElement('script');
      scriptTag.setAttribute(
        'src',
        'https://cmp.osano.com/16CHyLSGtDs7brYI/e2e2db52-5767-400f-b9fc-e4fab3cab972/osano.js'
      );

      scriptTag.onload = () => {
        observer.next(true); // Emit true when script loads successfully
        observer.complete(); // Complete the observable
      };

      scriptTag.onerror = () => {
        console.error('Failed to load Osano script.');
        observer.error('Failed to load Osano script.');
      };

      // Append the script tag to the document body
      document.body.appendChild(scriptTag);

      // Adjust the icon if needed
      if (offsetIcon) {
        document.body.classList.add('osano-icon-offset');
      }

      // Optional teardown logic in case the observable is unsubscribed
      return () => {
        scriptTag.onload = null;
        scriptTag.onerror = null;
      };
    });
  }

  public loadGoogleAnalytics(trackingId: string) {
    // Load the Google Analytics script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
    document.head.appendChild(script);

    // Initialize dataLayer and gtag function
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;
    window.gtag('js', new Date());
    window.gtag('config', trackingId, {
      cookie_domain: 'auto',
      cookie_expires: 31536000, // Time for 12 months in seconds
      cookie_prefix: 'degreed',
      page: window.location.pathname,
      anonymize_ip: true,
    });
  }

  private initializeAnalyticsTracking(authUser: AuthUser) {
    // Google Analytics Tracking
    if (this.googleAnalyticsIsRequired(authUser)) {
      const initGoogleAnalytics = () => {
        this.loadGoogleAnalytics(
          authUser?.defaultOrgInfo.settings.clientGoogleAnalyticsId
        );
      };

      if (window.Osano) {
        window.Osano.cm.addEventListener(
          'osano-cm-consent-saved',
          function (consentObject) {
            if (consentObject.OPT_OUT !== 'ACCEPT') {
              initGoogleAnalytics();
            }
          }
        );
      } else {
        initGoogleAnalytics();
      }
    }
  }

  private osanoIsRequired(authUser: AuthUser) {
    return authUser?.enableOsanoCookieBannerAuthed;
  }

  private googleAnalyticsIsRequired(authUser: AuthUser) {
    return (
      authUser?.defaultOrgInfo?.settings.disableAnalytics === false &&
      authUser?.defaultOrgInfo?.settings.clientGoogleAnalyticsId
    );
  }
}
